import type JobWorkSummaryVM from 'ab-viewModels/workRequest/jobWorkSummary.viewModel';

import * as TimeUtils from '@acceligentllc/shared/utils/time';
import { convertUnits } from '@acceligentllc/shared/utils/unitConversion';

import { QuantityUnitMap } from '@acceligentllc/shared/enums/quantityUnit';
import * as ReportBlockFieldEnum from '@acceligentllc/shared/enums/reportBlockField';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import { MAX_BILLABLE_WORK_DEFINITION_FIELDS, MAX_BILLABLE_WORK_INFORMATION_FIELDS } from 'ab-common/constants/value';

export const toCSVData = (jobWorkSummaries: JobWorkSummaryVM[]): string[][] => {
	const maxNumberOfDefFields = resolveMaxNumberOfDefFieldsForTable(jobWorkSummaries);
	const maxNumberOfInfoFields = resolveMaxNumberOfInfoFieldsForTable(jobWorkSummaries);
	const headers = resolveJobWorkSummaryTableHeadersForCSVExport(maxNumberOfDefFields, maxNumberOfInfoFields);

	const rows: string[][] = jobWorkSummaries.map((_jws) => {
		const definitionFieldsValues: string[] = [];
		const informationalFieldsValues: string[] = [];
		for (let i = 0; i < maxNumberOfDefFields; i++) {
			const defField = !!_jws?.[`definitionField${i + 1}Value`]
				? _resolveFieldFullValue(
					_jws?.[`definitionField${i + 1}Value`],
					_jws?.[`definitionField${i + 1}Name`],
					_jws?.[`definitionField${i + 1}Unit`]
				) : '';
			definitionFieldsValues.push(defField);
		}

		for (let i = 0; i < maxNumberOfInfoFields; i++) {
			const infoName = _jws?.[`informationField${i + 1}Name`] ?? null;
			const infoUnit = _jws?.[`informationField${i + 1}Unit`] ?? null;
			const infoValue = _jws?.[`informationField${i + 1}Value`] ?? '';
			const timezone = _jws?.timezone ?? 'UTC';

			if (typeof infoValue === 'string') {
				const isAddress = infoValue.includes('aa1');
				let isEquipment = false;
				let isSignature = false;

				if (typeof infoValue === 'string' && infoValue.includes('"id"')) {
					if (infoValue.includes('"signedAt"')) {
						isSignature = true;
					} else {
						isEquipment = true;
					}
				}

				if (isAddress) {
					let listOfAddress = '';
					// Extract objects so they can be parsed
					const addresses = infoValue.match(/\{.*?\}/g) ?? [];
					for (const _add of addresses) {
						const addressParsed = JSON.parse(_add);
						if (addressParsed.street) {
							listOfAddress = listOfAddress.length ? listOfAddress.concat('; ', addressParsed.street) : addressParsed.street;
						}
					}
					informationalFieldsValues.push(_resolveFieldFullValue(listOfAddress, infoName, null));
				} else if (isEquipment) {
					const equipments = infoValue.match(/\{.*?\}/g) ?? [];

					if (equipments.length) {
						const equipmentValues = equipments.map((_eq: string) => {
							const equipmentParsed = JSON.parse(_eq);
							if (!equipmentParsed.id) {
								return 'N/A';
							} else {
								return `${equipmentParsed.code} ${equipmentParsed.specification ? `(${equipmentParsed.specification})` : ''} `;
							}
						}).join('; ');
						informationalFieldsValues.push(_resolveFieldFullValue(equipmentValues, infoName, null));
					}
				} else if (isSignature) {
					let listOfSignatures = '';
					const signatures = infoValue.match(/\{.*?\}/g) ?? [];

					for (const _signature of signatures) {
						const signatureParsed = JSON.parse(_signature);

						if (signatureParsed.name && signatureParsed.imageUrl) {
							const timeStamp = `${TimeUtils.formatDate(signatureParsed.signedAt, TimeFormat.FULL_DATE, TimeFormat.ISO_DATETIME, timezone)}, ${TimeUtils.abbreviateTimeZone(timezone, 'z (Z)')}`;
							if (listOfSignatures.length) {
								listOfSignatures = listOfSignatures.concat('; ', `${signatureParsed.name} ${timeStamp}`);
							} else {
								listOfSignatures = `${signatureParsed.name} ${timeStamp}`;
							}
						}
					}
					informationalFieldsValues.push(_resolveFieldFullValue(listOfSignatures, infoName, null));
				} else {
					if (infoUnit && infoUnit.split(', ').length > 1 && infoUnit.split(', ').length === infoValue.split(', ').length) {
						const mergedValues = infoValue.split(', ').map((_val, _i) => `${_val} ${infoUnit.split(', ')[_i]}`).join(', ');
						informationalFieldsValues.push(_resolveFieldFullValue(mergedValues, infoName, null));
					} else {
						informationalFieldsValues.push(_resolveFieldFullValue(infoValue, infoName, infoUnit));
					}
				}
			}
		}

		// If field type is anything other then boolean or dropdown, jws "type" and "typeValue" are the same - we save it like that in DB for some reason
		let workTypeAdjustedName = '';
		if (_jws.typeFieldType === ReportBlockFieldEnum.Type.BOOLEAN || _jws.typeFieldType === ReportBlockFieldEnum.Type.DROPDOWN) {
			workTypeAdjustedName = `: ${_jws.typeValue}`;
		} else if (_jws.typeFieldType === ReportBlockFieldEnum.Type.EQUIPMENT) {
			if (_jws.typeValue?.includes('"id":') && _jws.typeValue.includes('"code":')) {
				const equipmentValueParsed = JSON.parse(_jws.typeValue);
				if (equipmentValueParsed.code) {
					workTypeAdjustedName = `: ${equipmentValueParsed.code} ${equipmentValueParsed.specification ?? ''}`;
				}
			} else {
				workTypeAdjustedName = `: ${_jws.typeValue}`;
			}
		}

		return [
			_jws.customerId ?? '',
			_jws.workRequest,
			_jws.revenue ? `${_jws.revenue.toFixed(2)}` : '',
			_jws.workOrderCode ?? '',
			_jws.startDate,
			_jws.reviewStatus ?? '',
			_jws.invoiceId ?? '',
			_jws.work,
			`${_jws.type} ${workTypeAdjustedName}`,
			...definitionFieldsValues,
			...informationalFieldsValues,
			_jws.quantity + '',
			_jws.unit ?? '',
			_jws.group ?? '',
			_jws.unitPrice ? `${parseFloat(_jws.unitPrice.toFixed(4))}` : '',
			_calculateUnitPricePerQuantityUnit(_jws),
			_jws.description ?? '',
			_jws.comment ?? '',
		];
	});

	return [headers, ...rows];
};

const resolveMaxNumberOfDefFieldsForTable = (_jwss: JobWorkSummaryVM[]) => {
	const max = _jwss.reduce((_acc, _jws) => {
		let currentJwsMax = 0;
		for (let i = 0; i < MAX_BILLABLE_WORK_DEFINITION_FIELDS; i++) {
			if (!_jws?.[`definitionField${i + 1}Value`]) {
				break;
			}
			currentJwsMax++;
		}
		return (currentJwsMax > _acc) ? currentJwsMax : _acc;
	}, 0);
	return max;
};

const resolveMaxNumberOfInfoFieldsForTable = (_jwss: JobWorkSummaryVM[]) => {
	const max = _jwss.reduce((_acc, _jws) => {
		let currentJwsMax = 0;
		for (let i = 0; i < MAX_BILLABLE_WORK_INFORMATION_FIELDS; i++) {
			if (!_jws?.[`informationField${i + 1}Value`]) {
				break;
			}
			currentJwsMax++;
		}
		return (currentJwsMax > _acc) ? currentJwsMax : _acc;
	}, 0);
	return max;
};

const resolveJobWorkSummaryTableHeadersForCSVExport = (maxNumberOfDefFields: number, maxNumberOfInfoFields: number) => {
	const defFields: string[] = [];
	const infoFields: string[] = [];
	for (let i = 0; i < maxNumberOfDefFields; i++) {
		defFields.push(`Definition Field ${i + 1}`);
	}
	for (let i = 0; i < maxNumberOfInfoFields; i++) {
		infoFields.push(`Information Field ${i + 1}`);
	}

	return [
		'Billing Code',
		'Job Name',
		'Revenue',
		'Work Order',
		'Start Date',
		'Review Status',
		'Invoice Id',
		'Work',
		'Type',
		...defFields,
		...infoFields,
		'Quantity',
		'Unit',
		'Group',
		'Unit Price per billingCode Unit ($)',
		'Unit Price per quantity Unit ($)',
		'Description',
		'Comment',
	];
};

const _resolveFieldFullValue = (value: string, name: Nullable<string>, unit: Nullable<string>) => {
	if (!name) {
		return unit ? `${value} ${unit}` : value;
	}

	return unit ? `${name}: ${value} ${unit}` : `${name}: ${value}`;
};

const _calculateUnitPricePerQuantityUnit = (jws: JobWorkSummaryVM) => {
	if (!jws.unitPrice || !jws.unit || !jws?.billingCode?.unit) {
		return '';
	}

	// we flipped this in VM
	const unit = jws.unit.replace('/', '_');

	if (QuantityUnitMap[unit] === QuantityUnitMap[jws.billingCode.unit]) {
		return `${parseFloat(jws.unitPrice.toFixed(4))}`;
	}

	const convertedUnit = convertUnits(jws.unitPrice, QuantityUnitMap[unit], QuantityUnitMap[jws.billingCode.unit]);

	return convertedUnit ? `${parseFloat(convertedUnit.toFixed(8))}` : '';
};
